import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MessageSearch from '../components/MessageSearch';
import FileUpload from '../components/FileUpload';
import ChatImage from '../components/ChatImage';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { Send, Search, MoreVertical, Trash2, Archive, CheckCheck, Ban } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessagesPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const openConversationWith = location.state?.openConversationWith;
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const messagesEndRef = useRef(null);

  // WebSocket hook
  const {
    socket,
    isConnected,
    messages: wsMessages,
    typingUsers,
    sendMessage: wsSendMessage,
    sendTypingIndicator,
    setMessages: setWsMessages
  } = useWebSocket(selectedConversation?.id, user);

  // Synchronize WebSocket messages with local state
  useEffect(() => {
    if (wsMessages.length > 0) {
      setMessages(wsMessages);
    }
  }, [wsMessages]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load messages when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
      // Mark all received messages as read (notifies sender via Socket.IO)
      const token = localStorage.getItem('token');
      if (token) {
        axios.post(
          `${API_URL}/api/conversations/${selectedConversation.id}/mark-read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => { /* non-blocking */ });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  // Listen for read receipts from the other participant via socket
  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      if (!data?.messageIds?.length) return;
      const ids = new Set(data.messageIds);
      setMessages((prev) => prev.map((m) => (ids.has(m.id) ? { ...m, read: true } : m)));
      setWsMessages((prev) => prev.map((m) => (ids.has(m.id) ? { ...m, read: true } : m)));
    };
    socket.on('messages_read', handler);
    return () => socket.off('messages_read', handler);
  }, [socket, setWsMessages]);

  // Open conversation automatically if coming from profile
  useEffect(() => {
    if (openConversationWith && user) {
      handleOpenConversationWith(openConversationWith);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConversationWith, user]);

  // Load conversations from API
  const loadConversations = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transform API response to match UI expectations
      const transformedConversations = response.data.map(conv => ({
        id: conv.id,
        user: conv.other_user || {
          id: null,
          name: 'Utilisateur inconnu',
          avatar: null
        },
        lastMessage: conv.last_message || 'Aucun message',
        time: formatTime(conv.last_message_at),
        unread: 0  // TODO: Implement unread count
      }));
      
      setConversations(transformedConversations);
      console.log('📋 Conversations chargées:', transformedConversations.length);
    } catch (error) {
      console.error('❌ Erreur chargement conversations:', error);
      toast.error('Erreur', { description: 'Impossible de charger les conversations' });
    } finally {
      setIsLoading(false);
    }
  };

  // Load messages from WebSocket (handled by useWebSocket hook)
  const loadMessages = (conversationId) => {
    console.log('🔄 Chargement messages pour conversation:', conversationId);
    // Messages are loaded automatically via WebSocket when joining conversation
    // The useWebSocket hook handles this
  };

  // Handle opening conversation with a specific user
  const handleOpenConversationWith = async (otherUser) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      // Create or get existing conversation
      const response = await axios.post(
        `${API_URL}/api/conversations`,
        { receiver_id: otherUser.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const conv = response.data;
      
      // Transform to UI format
      const transformedConv = {
        id: conv.id,
        user: conv.other_user || {
          id: otherUser.id,
          name: otherUser.name,
          avatar: otherUser.avatar || otherUser.profile_picture_url
        },
        lastMessage: conv.last_message || 'Nouvelle conversation',
        time: formatTime(conv.last_message_at),
        unread: 0
      };
      
      // Check if conversation already in list
      const existingIndex = conversations.findIndex(c => c.id === transformedConv.id);
      if (existingIndex === -1) {
        setConversations([transformedConv, ...conversations]);
      }
      
      setSelectedConversation(transformedConv);
      toast.success('Conversation ouverte', { description: `Avec ${otherUser.name}` });
    } catch (error) {
      console.error('❌ Erreur création conversation:', error);
      toast.error('Erreur', { description: 'Impossible de créer la conversation' });
    } finally {
      setIsLoading(false);
    }
  };

  // Format time helper
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Maintenant';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    // Send via WebSocket if connected
    if (isConnected && socket) {
      wsSendMessage(newMessage.trim());
      toast.success('Message envoyé');
    } else {
      toast.error('Erreur', { description: 'Connexion WebSocket perdue' });
    }

    setNewMessage('');
    sendTypingIndicator(false);
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;
    
    if (window.confirm('Voulez-vous supprimer toute cette conversation ?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${API_URL}/api/conversations/${selectedConversation.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Remove from local state
        setConversations(conversations.filter(c => c.id !== selectedConversation.id));
        setSelectedConversation(null);
        toast.success('Conversation supprimée');
      } catch (error) {
        console.error('❌ Erreur suppression conversation:', error);
        toast.error('Erreur', { description: 'Impossible de supprimer la conversation' });
      }
    }
  };

  const handleArchiveConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/conversations/${selectedConversation.id}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from list (archived conversations hidden by default)
      setConversations(conversations.filter(c => c.id !== selectedConversation.id));
      setSelectedConversation(null);
      toast.success('Conversation archivée');
    } catch (error) {
      console.error('❌ Erreur archivage conversation:', error);
      toast.error('Erreur', { description: 'Impossible d\'archiver la conversation' });
    }
  };

  const handleMarkAsUnread = async () => {
    if (!selectedConversation) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/conversations/${selectedConversation.id}/mark-unread`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Marqué comme non lu');
      // Reload conversations to update unread count
      loadConversations();
    } catch (error) {
      console.error('❌ Erreur marquer comme non lu:', error);
      toast.error('Erreur', { description: 'Impossible de marquer comme non lu' });
    }
  };

  const handleBlockUser = async () => {
    if (!selectedConversation) return;
    
    const otherUserId = selectedConversation.user.id;
    const otherUserName = selectedConversation.user.name;
    
    if (window.confirm(`Voulez-vous bloquer ${otherUserName} ?\n\nVous ne pourrez plus recevoir de messages de cette personne.`)) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API_URL}/api/users/${otherUserId}/block`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Remove conversation
        setConversations(conversations.filter(c => c.id !== selectedConversation.id));
        setSelectedConversation(null);
        toast.success(`${otherUserName} a été bloqué`);
      } catch (error) {
        console.error('❌ Erreur blocage utilisateur:', error);
        toast.error('Erreur', { description: 'Impossible de bloquer l\'utilisateur' });
      }
    }
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedMessages([]);
  };

  const selectAllMessages = () => {
    if (selectedMessages.length === messages.length) {
      // Deselect all
      setSelectedMessages([]);
    } else {
      // Select all
      setSelectedMessages(messages.map(m => m.id));
    }
  };

  const toggleMessageSelection = (messageId) => {
    if (selectedMessages.includes(messageId)) {
      setSelectedMessages(selectedMessages.filter(id => id !== messageId));
    } else {
      setSelectedMessages([...selectedMessages, messageId]);
    }
  };

  const deleteSelectedMessages = async () => {
    if (selectedMessages.length === 0) {
      toast.error('Aucun message sélectionné');
      return;
    }
    
    // Ask user if they want to delete for everyone or just for themselves
    const deleteForEveryone = window.confirm(
      `Supprimer ${selectedMessages.length} message(s) pour tout le monde ?\n\n` +
      'Cliquez sur "OK" pour supprimer pour tous\n' +
      'Cliquez sur "Annuler" pour supprimer uniquement pour vous'
    );
    
    try {
      const token = localStorage.getItem('token');
      const deleteType = deleteForEveryone ? 'for_everyone' : 'for_me';
      
      await axios.post(
        `${API_URL}/api/messages/delete`,
        {
          message_ids: selectedMessages,
          conversation_id: selectedConversation?.id,
          delete_type: deleteType
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from local state
      setMessages(messages.filter(msg => !selectedMessages.includes(msg.id)));
      setWsMessages(wsMessages.filter(msg => !selectedMessages.includes(msg.id)));
      setSelectedMessages([]);
      setSelectionMode(false);
      
      toast.success(`${selectedMessages.length} message(s) supprimé(s)`);
      
      // Reload conversations to update last message
      loadConversations();
    } catch (error) {
      console.error('❌ Erreur suppression messages:', error);
      toast.error('Erreur', { description: 'Impossible de supprimer les messages' });
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-blue-200">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-red-500 bg-clip-text text-transparent mb-6">
            💬 Messagerie
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-12rem)]">
            {/* Liste des conversations */}
            <Card className="lg:col-span-1 overflow-hidden flex flex-col">
              <CardHeader className="border-b">
                <CardTitle className="text-lg">Conversations</CardTitle>
                <div className="mt-2">
                  <Input
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0 flex-1 overflow-y-auto">
                {filteredConversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`w-full p-4 hover:bg-purple-50 transition-colors border-b flex items-start gap-3 ${
                      selectedConversation?.id === conv.id ? 'bg-purple-100' : ''
                    }`}
                  >
                    <Avatar>
                      <AvatarImage src={conv.user.avatar} />
                      <AvatarFallback>
                        {conv.user.name.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-left">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-sm">{conv.user.name}</p>
                        <span className="text-xs text-gray-500">{conv.time}</span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{conv.lastMessage}</p>
                    </div>
                    {conv.unread > 0 && (
                      <Badge className="bg-red-500">{conv.unread}</Badge>
                    )}
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Zone de chat */}
            <Card className="lg:col-span-2 overflow-hidden flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Header du chat */}
                  <CardHeader className="border-b">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={selectedConversation.user.avatar} />
                          <AvatarFallback>
                            {selectedConversation.user.name.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold">{selectedConversation.user.name}</p>
                          <div className="flex items-center gap-2">
                            {/* Connection status */}
                            <Badge 
                              className={`text-xs ${isConnected ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500'}`}
                            >
                              <span className={`inline-block w-2 h-2 rounded-full mr-1 bg-white ${isConnected ? 'animate-pulse' : ''}`}></span>
                              {isConnected ? 'Connecté' : 'Hors ligne'}
                            </Badge>
                            {/* Typing indicator */}
                            {typingUsers.length > 0 && (
                              <span className="text-xs text-gray-600 italic animate-pulse">
                                ⌨️ En train d'écrire...
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!selectionMode ? (
                          <>
                            {/* Search button */}
                            <Button 
                              variant="outline" 
                              size="icon"
                              className="hover:bg-gray-50"
                              onClick={() => setShowSearch(!showSearch)}
                            >
                              <Search className="w-5 h-5 text-gray-600" />
                            </Button>

                            {/* Mode sélection button */}
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="hover:bg-purple-50 border-purple-300"
                              onClick={toggleSelectionMode}
                            >
                              <CheckCheck className="w-4 h-4 mr-2" />
                              Sélectionner
                            </Button>
                            
                            {/* Delete entire conversation button */}
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="hover:bg-red-50 border-red-300 text-red-600"
                              onClick={handleDeleteConversation}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer conversation
                            </Button>
                          </>
                        ) : (
                          <>
                            {/* Selection mode actions */}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={selectAllMessages}
                              className="hover:bg-blue-50"
                            >
                              Tout sélectionner
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                if (selectedMessages.length === 0) {
                                  toast.warning('Aucun message sélectionné');
                                  return;
                                }
                                if (window.confirm(`Supprimer ${selectedMessages.length} message(s) pour vous uniquement ?`)) {
                                  // Use the existing deleteSelectedMessages with "for_me"
                                  deleteSelectedMessages();
                                }
                              }}
                              className="hover:bg-orange-50 border-orange-300 text-orange-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Pour moi ({selectedMessages.length})
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                if (selectedMessages.length === 0) {
                                  toast.warning('Aucun message sélectionné');
                                  return;
                                }
                                // Delete for everyone - calls deleteSelectedMessages which prompts for type
                                deleteSelectedMessages();
                              }}
                              className="hover:bg-red-50 border-red-500 text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Pour tous ({selectedMessages.length})
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectionMode(false);
                                setSelectedMessages([]);
                              }}
                            >
                              Annuler
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  {/* Message Search */}
                  {showSearch && (
                    <MessageSearch
                      messages={messages}
                      onSelectMessage={(messageId) => {
                        const element = document.getElementById(`message-${messageId}`);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          element.classList.add('bg-yellow-100');
                          setTimeout(() => {
                            element.classList.remove('bg-yellow-100');
                          }, 2000);
                        }
                      }}
                    />
                  )}

                  {/* Messages */}
                  <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                    {selectionMode && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
                        <span className="text-sm text-blue-700 font-medium">
                          Mode sélection : {selectedMessages.length} message(s) sélectionné(s)
                        </span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectionMode(false);
                            setSelectedMessages([]);
                          }}
                        >
                          Annuler
                        </Button>
                      </div>
                    )}
                    
                    {messages.map((msg) => {
                      // Determine if message is from current user
                      const isMyMessage = msg.sender?.id === user?.id || msg.sender_id === user?.id || msg.sender === 'me';
                      
                      return (
                        <div
                          key={msg.id}
                          id={`message-${msg.id}`}
                          className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'} transition-colors duration-300`}
                          onClick={() => selectionMode && toggleMessageSelection(msg.id)}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg transition-all ${
                              selectionMode ? 'cursor-pointer' : ''
                            } ${
                              isMyMessage
                                ? `bg-purple-600 text-white ${
                                    selectedMessages.includes(msg.id) ? 'ring-4 ring-yellow-400 scale-105' : ''
                                  }`
                                : `bg-white border ${
                                    selectedMessages.includes(msg.id) ? 'ring-4 ring-yellow-400 scale-105' : ''
                                  }`
                            }`}
                          >
                            {/* Show image preview if message has image */}
                            {msg.file_url && (
                              <div className="mb-2">
                                <ChatImage
                                  fileUrl={msg.file_url}
                                  alt={msg.file_name || 'image'}
                                  className="max-w-full rounded cursor-pointer hover:opacity-90"
                                />
                              </div>
                            )}
                            <p className="text-sm">{msg.content}</p>
                            <div className={`flex items-center gap-1 mt-1 ${isMyMessage ? 'justify-end' : 'justify-start'}`}>
                              <p
                                className={`text-xs ${
                                  isMyMessage ? 'text-purple-200' : 'text-gray-500'
                                }`}
                              >
                                {msg.time || (msg.created_at ? new Date(msg.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'}) : '')}
                              </p>
                              {isMyMessage && (
                                <CheckCheck
                                  className={`w-4 h-4 ${msg.read ? 'text-sky-300' : 'text-purple-300/70'}`}
                                  data-testid={msg.read ? 'msg-read' : 'msg-sent'}
                                  aria-label={msg.read ? 'Message lu' : 'Message envoyé'}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </CardContent>

                  {/* Input message */}
                  <div className="border-t p-4">
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <FileUpload
                        conversationId={selectedConversation.id}
                        onFileUploaded={(data) => {
                          // Reload messages to show uploaded file
                          loadMessages(selectedConversation.id);
                        }}
                      />
                      <Input
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          if (e.target.value && isConnected) {
                            sendTypingIndicator(true);
                          } else if (!e.target.value) {
                            sendTypingIndicator(false);
                          }
                        }}
                        onBlur={() => sendTypingIndicator(false)}
                        placeholder="Écrire un message..."
                        className="flex-1"
                        disabled={!isConnected}
                      />
                      <Button 
                        type="submit" 
                        className="bg-purple-600"
                        disabled={!isConnected || !newMessage.trim()}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  Sélectionnez une conversation pour commencer
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MessagesPage;
